import { toMilliseconds, type SmarticoMission, type SmarticoTask } from "./types";

/* Choosing which mission the sidebar shows, and what it says.

   Kept pure and separate from the hooks so the selection rules are testable
   without a browser, a session or a Smartico label — the same split
   `api/mission-progress.ts` uses for the app-owned fallback. */

export type MissionView = {
  id: number;
  name: string;
  imageUrl: string;
  /* 0–100, clamped and rounded. */
  percent: number;
  /* When the mission closes, or null if it never does. */
  deadlineMs: number | null;
  /* "3/10" when Smartico asks for a count, otherwise null and the bar shows a
     percentage instead. */
  countLabel: string | null;
};

/* A mission the player cannot act on is not a mission to advertise: completed
   and locked ones are out, and one that must be joined is out until they join.
   Windows are checked in real time because a mission can open or close while
   the page is sitting there. */
const isOffered = (mission: SmarticoMission, nowMs: number) => {
  if (mission.type !== "mission") return false;
  if (mission.is_completed || mission.is_locked) return false;
  if (mission.is_requires_optin && !mission.is_opted_in) return false;

  const from = toMilliseconds(mission.active_from_ts ?? 0);
  const till = toMilliseconds(mission.active_till_ts ?? 0);

  if (from > 0 && nowMs < from) return false;
  if (till > 0 && nowMs > till) return false;

  const limit = timeLimitDeadline(mission);

  return limit === null || nowMs <= limit;
};

/* Two different clocks can end a mission. `active_till_ts` is a date everyone
   shares; `time_limit_ms` is a per-player countdown that starts at `dt_start`,
   so two players can see different numbers on the same mission. */
const timeLimitDeadline = (mission: SmarticoMission) => {
  const limit = mission.time_limit_ms ?? 0;
  const start = mission.dt_start ?? 0;

  return limit > 0 && start > 0 ? toMilliseconds(start) + limit : null;
};

const deadlineFor = (mission: SmarticoMission) => {
  const limit = timeLimitDeadline(mission);
  const till = mission.active_till_ts && mission.active_till_ts > 0
    ? toMilliseconds(mission.active_till_ts)
    : null;

  if (limit === null) return till;
  if (till === null) return limit;

  /* Whichever runs out first is the one the player actually has. */
  return Math.min(limit, till);
};

/* Smartico marks some tasks as "show me as a count" — "3 of 10 spins" reads
   better than "30%". The flag is missing from the vendor's own type, so it is
   read off an optional field rather than trusted to exist. Only a mission with
   exactly one task can be summarised this way; anything else is a percentage. */
const countLabelFor = (tasks: SmarticoTask[] | undefined) => {
  if (!tasks || tasks.length !== 1) return null;

  const task = tasks[0];
  const expected = task.execution_count_expected ?? 0;

  if (!task.display_progress_as_count || expected <= 0) return null;

  const actual = Math.min(Math.max(task.execution_count_actual ?? 0, 0), expected);

  return `${actual}/${expected}`;
};

const clampPercent = (value: number) =>
  Number.isFinite(value) ? Math.round(Math.min(Math.max(value, 0), 100)) : 0;

/* `position` is Smartico's own ordering, and the first offered mission in it is
   the one to show. Missions without a position sort last rather than first —
   an unset field should not outrank a deliberate one. */
export const selectMission = (
  missions: SmarticoMission[] | undefined,
  nowMs: number,
): MissionView | null => {
  if (!missions || missions.length === 0) return null;

  const mission = [...missions]
    .sort(
      (left, right) =>
        (left.position ?? Number.MAX_SAFE_INTEGER) - (right.position ?? Number.MAX_SAFE_INTEGER),
    )
    .find((candidate) => isOffered(candidate, nowMs));

  if (!mission) return null;

  return {
    id: mission.id,
    name: mission.name,
    imageUrl: mission.image,
    percent: clampPercent(mission.progress),
    deadlineMs: deadlineFor(mission),
    countLabel: countLabelFor(mission.tasks),
  };
};

/* Whether any mission in the feed has a clock at all. Without one there is
   nothing for a ticker to re-check, so the widget does not run one. */
export const hasTimedMission = (missions: SmarticoMission[] | undefined) =>
  (missions ?? []).some(
    (mission) =>
      (mission.active_from_ts ?? 0) > 0 ||
      (mission.active_till_ts ?? 0) > 0 ||
      ((mission.time_limit_ms ?? 0) > 0 && (mission.dt_start ?? 0) > 0),
  );
